<template>
    <div class="w-full space-y-3">
        <Head title="Supplier Invoices"/>
        <div class="flex justify-between">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Supplier Invoices Requiring Review</h1>
        </div>
        <br/>
        <div class="w-full flex justify-between">
            <div class="flex w-full -mr-32">
                <v-select class="mt-1 block w-2/3 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                    :options="team_managers" label="name" v-model="form.filter.team_manager" placeholder="Team Manager" />
            </div>
            <div class="flex w-full -ml-4">
                <search-filter :show-filter="false" v-model:value="form.search" class="w-full" @reset="reset"></search-filter>
            </div>
            <div class="flex w-full justify-end justify-content-end align-items-end">
                <a :href="route('frontend.supplier-invoices.supplier_invoices_export', {
                    _query: {
                        search: form.search,
                        team_manager: form.filter.team_manager
                    }})" class="py-3 px-3 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral w-40 text-center">
                    Export All
                </a>
            </div>
        </div>
        <br/>
        <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6">
            <div class="flex flex-col mt-3 space-y-3">
                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-gray-50">
                                    <tr>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Reference
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Fixture
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Team Manager
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Invoice Number
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Invoiced Date
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Traveller Name
                                        </th>
                                        <th scope="col"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="supplier_invoices.data.length > 0" v-for="(supplier_invoice, index) in supplier_invoices.data" :key="index" :class="{'bg-gray-100': index % 2, 'bg-orange-100': supplier_invoice.amount_disputed > 0}" class="cursor-pointer">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <a :href="route('frontend.fixture.supplier-invoice.index' , supplier_invoice.fixture.id)" target="_blank">
                                                {{ supplier_invoice.fixture.reference }}
                                            </a>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <a :href="route('frontend.fixture.supplier-invoice.index' , supplier_invoice.fixture.id)" target="_blank">
                                                {{ supplier_invoice.fixture.title }}
                                            </a>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <a :href="route('frontend.fixture.supplier-invoice.index' , supplier_invoice.fixture.id)" target="_blank">
                                                {{ supplier_invoice.fixture.travel_manager.first_name }} {{ supplier_invoice.fixture.travel_manager.last_name }}
                                            </a>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <a :href="route('frontend.fixture.supplier-invoice.index' , supplier_invoice.fixture.id)">
                                                {{ supplier_invoice.invoice_number }}
                                            </a>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <a :href="route('frontend.fixture.supplier-invoice.index' , supplier_invoice.fixture.id)">
                                                {{ moment(supplier_invoice.received_at, 'YYYY-MM-DD').format('DD/MM/YYYY') }}
                                            </a>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <a :href="route('frontend.fixture.supplier-invoice.index' , supplier_invoice.fixture.id)">
                                                {{ formatCurrency((supplier_invoice.amount_disputed > 0 ? supplier_invoice.amount_disputed : supplier_invoice.amount), supplier_invoice.currency?.code ?? 'GBP', true) }}
                                            </a>
                                            <div v-if="supplier_invoice.amount_disputed > 0" class="text-xs">
                                                Disputed: {{ formatCurrency((supplier_invoice.amount - supplier_invoice.amount_disputed), supplier_invoice.currency?.code ?? 'GBP', true) }}
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <a :href="route('frontend.fixture.supplier-invoice.index' , supplier_invoice.fixture.id)">
                                                {{ supplier_invoice.lead_passenger ?? '' }}
                                            </a>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <button v-if="supplier_invoice.review_required" type="button" @click="emitter.emit('openReviewInvoice', supplier_invoice)"
                                                    class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-gray-500 hover:bg-gray-700">
                                                <EyeIcon class="w-4 h-4 mr-2" />
                                                Review
                                            </button>
                                            <button v-if="supplier_invoice.approval_required == 1" type="button" @click="emitter.emit('openApproveInvoice', supplier_invoice)"
                                                    class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-orange-400 hover:bg-orange-900">
                                                <CheckCircleIcon class="w-4 h-4 mr-2" />
                                                Approve
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-else>
                                        <td colspan="2"
                                            class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            There are no fixtures available for your search criteria.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <Pagination :links="supplier_invoices.links"/>
            </div>
        </div>
    </div>
    <ReviewSupplierInvoice ref="review_supplier"/>
    <ApproveSupplierInvoice ref="approve_supplier"/>
</template>

<script>
import axios from "axios";
import moment from "moment";
import {ref, computed} from "vue";
import pickBy from "lodash/pickBy";
import {emitter} from "@/eventHub";
import debounce from "lodash/debounce";
import Pagination from "@/Components/Pagination";
import Authenticated from "@/Layouts/Authenticated";
import SearchFilter from "@/Components/SearchFilter";
import {Head, useForm, usePage} from "@inertiajs/vue3";
import SlideOutErrors from "@/Components/SlideOutErrors";
import {UploadIcon, CheckCircleIcon, EyeIcon} from '@heroicons/vue/solid';
import ReviewSupplierInvoice from "@/Components/SlideOuts/ReviewSupplierInvoice";
import ApproveSupplierInvoice from "@/Components/SlideOuts/ApproveSupplierInvoice";

export default {
    layout: Authenticated,
    components: {
        Head,
        EyeIcon,
        Pagination,
        UploadIcon,
        SearchFilter,
        SlideOutErrors,
        CheckCircleIcon,
        ReviewSupplierInvoice,
        ApproveSupplierInvoice
    },
    props: [
        'supplier_invoices',
        'filters'
    ],
    created() {
        this.moment = moment
    },
    data() {
        return {
            showErrors: false,
            emitter:emitter,
            form: useForm({
                search: null,
                filter: {
                    team_manager: ""
                }
            })
        }
    },
    methods: {
        reset() {
            this.form.search = null;
        }
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route('frontend.supplier-invoices.index'), pickBy(this.form), { preserveState: true, preserveScroll: true });
            }, 500),
        },
    },
    computed: {
        team_managers() {
            return this.filters.team_managers;
        }
    }
}
</script>
